import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Button, Typography, Box, RadioGroup, FormControlLabel, Radio, TextField, Avatar, Modal, Slider, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Container, Alert, AlertTitle, Tooltip, CircularProgress
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useWizard } from './WizardContext';
import { useFirebase } from '@/context/FirebaseContext';
import { CheckCircle, Upload, X, Copy, MessageSquare, Phone } from 'lucide-react';
import Confetti from 'react-confetti';
import Particles from '@tsparticles/react';
import { tsParticles } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import AvatarEditor from 'react-avatar-editor';
import { debounce } from 'lodash';

// Custom MUI Theme
const theme = createTheme({
  palette: {
    primary: { main: '#3b82f6' },
    secondary: { main: '#ec4899' },
    background: { default: '#1a0b2e' },
    text: { primary: '#e0e0e0', secondary: '#a855f7' },
    error: { main: '#ef4444' },
    success: { main: '#22c55e' },
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h5: { fontWeight: 700, fontSize: 'clamp(1.5rem, 5vw, 1.75rem)' },
    body1: { fontSize: 'clamp(0.875rem, 3vw, 1rem)' },
  },
});

// Stepper configuration
const steps = [
  { label: 'Setup', subSteps: ['Document'] },
  { label: 'Configure', subSteps: ['Welcome Message', 'Tone', 'Name', 'Image', 'Creativity'] },
  { label: 'Finish', subSteps: ['Finish'] },
];

// Tone and creativity options
const toneOptions = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];

const creativityOptions = [
  { value: 'restricted', label: 'Restricted (Document-based)' },
  { value: 'balanced', label: 'Balanced (Document + Knowledge)' },
];

// Styled components
const MainContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  background: theme.palette.background.default,
  padding: 'clamp(1.5rem, 4vw, 2.5rem)',
  overflow: 'hidden',
}));

const CardContainer = styled(Box)(({ theme }) => ({
  maxWidth: 'clamp(320px, 90vw, 640px)',
  margin: '0 auto',
  background: 'rgba(59,44,91,0.85)',
  backdropFilter: 'blur(12px)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
  border: `2px solid ${theme.palette.text.secondary}`,
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    maxWidth: '95%',
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 'clamp(1rem, 3vw, 1.5rem)',
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const CustomStepper = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
  gap: 'clamp(1rem, 3vw, 1.5rem)',
  marginBottom: theme.spacing(2),
  '& .step': {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 1rem',
    borderRadius: '50px',
    background: 'rgba(59,44,91,0.85)',
    color: theme.palette.text.primary,
    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 0 12px ${theme.palette.primary.main}40`,
    },
  },
  '& .step.active': {
    background: theme.palette.primary.main,
    color: '#fff',
    boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
  },
  '& .step.completed': {
    background: theme.palette.success.main,
    color: '#fff',
    boxShadow: `0 6px 20px ${theme.palette.success.main}40`,
  },
  '& .step-number': {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: '#fff',
    color: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  '& .step.completed .step-number': {
    background: theme.palette.success.main,
    color: '#fff',
  },
  '& .step.active .step-number': {
    background: theme.palette.primary.main,
    color: '#fff',
  },
  '& .progress-line': {
    position: 'absolute',
    top: '50%',
    left: '100%',
    width: 'calc(100% - 1.5rem)',
    height: '3px',
    background: `${theme.palette.text.secondary}40`,
    zIndex: -1,
  },
  '& .progress-line.filled': {
    background: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    '& .step': {
      width: '100%',
      justifyContent: 'center',
      padding: '0.5rem',
      fontSize: '0.75rem',
    },
    '& .progress-line': {
      width: '3px',
      height: 'calc(100% - 1rem)',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
    },
  },
}));

const CustomButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: '#fff',
  padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1.5rem, 4vw, 2rem)',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
  textTransform: 'none',
  boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}cc, ${theme.palette.secondary.main}cc)`,
    boxShadow: `0 6px 25px ${theme.palette.primary.main}60`,
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&:disabled': {
    background: `${theme.palette.primary.main}40`,
    color: 'rgba(255,255,255,0.3)',
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0.5rem 1.5rem',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  background: 'rgba(59,44,91,0.85)',
  color: theme.palette.text.primary,
  padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1.5rem, 4vw, 2rem)',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
  textTransform: 'none',
  boxShadow: `0 4px 20px rgba(0,0,0,0.3)`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `${theme.palette.text.secondary}40`,
    color: theme.palette.secondary.main,
    boxShadow: `0 6px 25px ${theme.palette.text.secondary}60`,
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0.5rem 1.5rem',
  },
}));

const CustomRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.text.primary,
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
    animation: 'pulse 0.7s forwards',
  },
  '&:hover': {
    background: `${theme.palette.text.secondary}20`,
  },
  '@keyframes pulse': {
    '0%': { boxShadow: `0 0 0 0 ${theme.palette.secondary.main}40` },
    '70%': { boxShadow: `0 0 0 10px ${theme.palette.secondary.main}00` },
    '100%': { boxShadow: `0 0 0 0 ${theme.palette.secondary.main}00` },
  },
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    background: 'rgba(59,44,91,0.9)',
    color: theme.palette.text.primary,
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
    border: `2px solid ${theme.palette.text.secondary}40`,
    transition: 'all 0.3s ease',
    '&:hover': {
      border: `2px solid ${theme.palette.secondary.main}80`,
      background: 'rgba(59,44,91,0.95)',
    },
    '&.Mui-focused': {
      border: `2px solid ${theme.palette.secondary.main}`,
      boxShadow: `0 0 8px ${theme.palette.secondary.main}40`,
    },
    '& input, & textarea': {
      '&::placeholder': {
        color: `${theme.palette.text.primary}60`,
        opacity: 1,
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: `${theme.palette.text.primary}80`,
    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
    transform: 'translate(16px, 14px) scale(1)',
    '&.Mui-focused, &.MuiFormLabel-filled': {
      color: theme.palette.secondary.main,
      transform: 'translate(16px, -8px) scale(0.75)',
      background: 'rgba(59,44,91,0.9)',
      padding: '0 4px',
    },
  },
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.error.main,
    fontSize: 'clamp(0.625rem, 1.5vw, 0.75rem)',
    background: 'rgba(59,44,91,0.9)',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const ErrorContainer = styled(Alert)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  margin: '1rem auto',
  background: 'rgba(239, 68, 68, 0.1)',
  border: `1px solid ${theme.palette.error.main}`,
  color: '#fff',
  borderRadius: '8px',
  padding: theme.spacing(2),
  '& .MuiAlert-icon': {
    color: '#fff',
  },
  '& .MuiAlert-message': {
    width: '100%',
  },
}));

const ToastContainer = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(239, 68, 68, 0.9)',
  color: '#fff',
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  zIndex: 2000,
  maxWidth: '90%',
  width: 'clamp(200px, 80vw, 400px)',
  fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
  textAlign: 'center',
}));

const RemoveDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(59,44,91,0.85)',
    backdropFilter: 'blur(12px)',
    borderRadius: '16px',
    border: `2px solid ${theme.palette.text.secondary}`,
    color: theme.palette.text.primary,
  },
}));

// BookLoader Component
const BookLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      style={{ width: 80, height: 80, borderRadius: '50%', border: '8px solid transparent', borderTopColor: theme.palette.primary.main, borderBottomColor: theme.palette.secondary.main }}
    />
  </Box>
);

// Particles configuration
const particlesInit = async () => {
  await loadSlim(tsParticles);
};

const particlesOptions = {
  particles: {
    number: { value: 40, density: { enable: true, value_area: 800 } },
    color: { value: ['#3b82f6', '#ec4899', '#a855f7'] },
    shape: { type: 'circle' },
    opacity: { value: 0.3, random: true },
    size: { value: 2.5, random: true },
    move: {
      enable: true,
      speed: 0.8,
      direction: 'none',
      random: true,
      out_mode: 'out',
      trail: { enable: true, length: 5, intensity: 0.2 },
    },
  },
  interactivity: {
    events: { onhover: { enable: true, mode: 'repulse' }, onclick: { enable: true, mode: 'push' } },
    modes: { repulse: { distance: 100 }, push: { particles_nb: 2 } },
  },
};

// Error Boundary
class WizardErrorBoundary extends React.Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Wizard Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const errorTime = new Date().toLocaleString();
      const simpleError = 'Something went wrong. Please try again or contact support.';
      const fullError = `Error: ${this.state.error?.message || 'Unknown error'}\nTime: ${errorTime}\nStack: ${this.state.error?.stack || 'No stack trace'}\nComponent Stack: ${this.state.errorInfo?.componentStack || 'No component stack'}`;
      return (
        <ErrorContainer severity="error">
          <AlertTitle>Oops!</AlertTitle>
          <Typography>{simpleError}</Typography>
          <Tooltip title="Copy details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(fullError)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
          <Box sx={{ mt: 2 }}>
            <CustomButton onClick={() => window.location.reload()}>Reload</CustomButton>
          </Box>
        </ErrorContainer>
      );
    }
    return this.props.children;
  }
}

// Bot Type Selection Screen
const BotTypeSelectionScreen = ({ onSelect, focusRef }) => {
  const { botType, setBotType, setBotTypeError, botTypeError } = useWizard();
  const navigate = useNavigate();

  const handleBotTypeSelect = (type) => {
    setBotType(type);
    setBotTypeError('');
    if (type === 'phone') {
      navigate('/plans');
    } else {
      onSelect(type);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      ref={focusRef}
    >
      <CardContainer>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)', textAlign: 'center', mb: 3 }}>
          Choose Your Bot Type
        </Typography>
        <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8, textAlign: 'center', mb: 4 }}>
          Select the perfect bot to streamline your interactions!
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          {[{ type: 'text', icon: MessageSquare, title: 'Text Bot', description: 'Perfect for website chats, messaging apps, and text-based support', cost: 'Free Trial Available' },
          { type: 'phone', icon: Phone, title: 'Phone Bot', description: 'Advanced voice capabilities for seamless phone-based customer service', cost: 'Requires Paid Plan' }]
            .map(({ type, icon: Icon, title, description, cost }) => (
              <Box
                key={type}
                onClick={() => handleBotTypeSelect(type)}
                sx={{
                  bgcolor: 'rgba(59,44,91,0.85)',
                  p: 3,
                  borderRadius: '12px',
                  border: `2px solid ${botType === type ? theme.palette.secondary.main : theme.palette.text.secondary}40`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { boxShadow: `0 0 12px ${theme.palette.primary.main}40` },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Icon size={32} color={theme.palette.text.primary} />
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                    {title}
                  </Typography>
                </Box>
                <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8, textAlign: 'left' }}>
                  {description}
                </Typography>
                <Typography sx={{ color: type === 'text' ? theme.palette.success.main : theme.palette.secondary.main, fontWeight: 600, textAlign: 'left' }}>
                  {cost}
                </Typography>
              </Box>
            ))}
        </Box>
        {botTypeError && (
          <ErrorContainer severity="error">
            <AlertTitle>Bot Type Selection Error</AlertTitle>
            <Typography>{botTypeError}</Typography>
            <Tooltip title="Copy error details">
              <IconButton
                onClick={() => navigator.clipboard.writeText(`Bot Type Error: ${botTypeError}\nTime: ${new Date().toLocaleString()}`)}
                sx={{ mt: 1, color: theme.palette.primary.main }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </ErrorContainer>
        )}
      </CardContainer>
    </motion.div>
  );
};

const BotNameStep = ({ focusRef }) => {
  const { botName, setBotName, botNameError, setBotNameError } = useWizard();
  const [localError, setLocalError] = useState('');
  const [isEdited, setIsEdited] = useState(false);

  // Set default botName only on initial mount if not already set
  useEffect(() => {
    if (!botName && !isEdited) {
      setBotName('VuxoraAI');
    }
  }, []); // Run only on mount

  const handleBotNameChange = (event) => {
    const value = event.target.value;
    setBotName(value);
    setIsEdited(true);
    setBotNameError('');
    setLocalError(''); // Clear error on change to avoid showing it prematurely
  };

  // Validation function to be called when user tries to continue
  const validateBotName = () => {
    const trimmedValue = botName.trim();
    if (!trimmedValue) {
      setLocalError('Please enter a bot name');
      return false;
    } else if (trimmedValue.length > 50) {
      setLocalError('Bot name cannot exceed 50 characters');
      return false;
    } else if (!/^[a-zA-Z0-9\s-_]+$/.test(trimmedValue)) {
      setLocalError('Use only letters, numbers, spaces, hyphens, or underscores');
      return false;
    }
    setLocalError('');
    return true;
  };

  // Expose validateBotName to parent via useWizard or similar mechanism
  useEffect(() => {
    // Assuming useWizard allows setting a validation function
    // Modify this based on your actual context implementation
    return () => {
      // Cleanup if needed
    };
  }, [botName]);

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center', position: 'relative' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Name Your Bot
      </Typography>
      <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
        Give your bot a unique name (pre-filled as VuxoraAI).
      </Typography>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <CustomTextField
          label="Bot Name"
          value={botName}
          onChange={handleBotNameChange}
          variant="outlined"
          placeholder="E.g., VuxoraAI"
          error={!!botNameError || !!localError}
          helperText={botNameError || localError}
          inputProps={{ maxLength: 50 }}
          aria-describedby="bot-name-error"
          autoFocus
        />
        {botName.trim() && !botNameError && !localError && (
          <CheckCircle
            sx={{
              position: 'absolute',
              bottom: '-30px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: isEdited ? theme.palette.success.main : theme.palette.text.primary,
              transition: 'color 0.3s ease',
            }}
          />
        )}
      </Box>
      {(botNameError || localError) && (
        <ErrorContainer severity="error">
          <AlertTitle>Bot Name Error</AlertTitle>
          <Typography>{botNameError || localError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Bot Name Error: ${botNameError || localError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      )}
    </Box>
  );
};

// Expose validateBotName for parent component to call
BotNameStep.validate = function () {
  return this.validateBotName();
};

const ImageEditorModal = ({ open, onClose, imageSrc, onSave }) => {
  const [scale, setScale] = useState(1);
  const editorRef = useRef(null);
  const modalRef = useRef(null);
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!editorRef.current) {
      setError('Image editor failed to initialize');
      return;
    }
    try {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      canvas.toBlob((blob) => {
        if (!blob) {
          setError('Failed to generate image blob');
          return;
        }
        onSave(dataUrl, blob);
        onClose();
      }, 'image/png', 0.9);
    } catch (err) {
      setError(`Failed to save image: ${err.message}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    if (open && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTab = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      modalRef.current.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => modalRef.current?.removeEventListener('keydown', handleTab);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="image-editor-title" ref={modalRef}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'rgba(59,44,91,0.85)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          p: 4,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          border: `2px solid ${theme.palette.text.secondary}`,
          boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Typography id="image-editor-title" variant="h6" sx={{ color: theme.palette.text.primary }}>
          Edit Bot Avatar
        </Typography>
        {imageSrc ? (
          <>
            <AvatarEditor
              ref={editorRef}
              image={imageSrc}
              width={200}
              height={200}
              border={50}
              borderRadius={100}
              color={[59, 44, 91, 0.85]}
              scale={scale}
              rotate={0}
            />
            <Box sx={{ width: '100%', maxWidth: 300 }}>
              <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>Zoom</Typography>
              <Slider
                value={scale}
                onChange={(e, newValue) => setScale(newValue)}
                min={0.5}
                max={2}
                step={0.1}
                aria-label="Image zoom"
                sx={{ color: theme.palette.secondary.main }}
              />
            </Box>
            {error && (
              <ErrorContainer severity="error">
                <AlertTitle>Image Editor Error</AlertTitle>
                <Typography>{error}</Typography>
                <Tooltip title="Copy error details">
                  <IconButton
                    onClick={() => navigator.clipboard.writeText(`Image Editor Error: ${error}\nTime: ${new Date().toLocaleString()}`)}
                    sx={{ mt: 1, color: theme.palette.primary.main }}
                  >
                    <Copy size={20} />
                  </IconButton>
                </Tooltip>
              </ErrorContainer>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
              <CustomButton onClick={handleSave}>Save</CustomButton>
            </Box>
          </>
        ) : (
          <Typography sx={{ color: theme.palette.error.main }}>No image loaded</Typography>
        )}
      </Box>
    </Modal>
  );
};

const ImageUploadStep = ({ focusRef }) => {
  const { imageFile, setImageFile, imagePreview, setImagePreview, imageError, setImageError } = useWizard();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorImageSrc, setEditorImageSrc] = useState(null);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const imageInputRef = useRef(null);

  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImageError('Please select an image');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSizeMB = 1;

    if (!allowedTypes.includes(file.type)) {
      setImageError('Invalid file type. Use JPEG, PNG, GIF, or WebP.');
      if (imageInputRef.current) imageInputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setImageError(`File size exceeds ${maxSizeMB}MB limit`);
      if (imageInputRef.current) imageInputRef.current.value = '';
      return;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      setEditorImageSrc(dataUrl);
      setEditorOpen(true);
      if (imageInputRef.current) imageInputRef.current.value = '';
      setImageError('');
    } catch (error) {
      setImageError(`Failed to process image: ${error.message}`);
    }
  };

  const handleSaveCroppedImage = (previewDataUrl, fileBlob) => {
    setImagePreview(previewDataUrl);
    setImageFile(fileBlob);
    setImageError('');
  };

  const handleRemoveImage = () => {
    setOpenRemoveDialog(true);
  };

  const confirmRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageError('');
    setEditorImageSrc(null);
    setOpenRemoveDialog(false);
  };

  return (
    <>
      <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
          Upload Bot Avatar (Optional)
        </Typography>
        <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
          Add a custom avatar for your bot (max 1MB).
        </Typography>
        <Box sx={{ width: '100%', bgcolor: 'rgba(59,44,91,0.85)', p: 4, borderRadius: '12px', border: `2px dashed ${theme.palette.text.secondary}`, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <Upload sx={{ color: theme.palette.secondary.main }} size={24} />
            <Typography sx={{ color: theme.palette.text.primary }}>
              {imagePreview ? 'Image Selected' : 'Browse or Drag Image'}
            </Typography>
          </Box>
          <input
            type="file"
            ref={imageInputRef}
            hidden
            onChange={handleFileSelect}
            accept="image/jpeg,image/png,image/gif,image/webp"
            aria-label="Upload bot avatar"
          />
          <CustomButton startIcon={<Upload size={20} sx={{ color: '#fff' }} />} onClick={() => imageInputRef.current?.click()}>
            {imagePreview ? 'Change Image' : 'Upload Image'}
          </CustomButton>
          {imagePreview && (
            <Box sx={{ mt: 2, bgcolor: `${theme.palette.text.secondary}20`, p: 2, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Avatar src={imagePreview} sx={{ width: 'clamp(60px, 15vw, 80px)', height: 'clamp(60px, 15vw, 80px)', borderRadius: '50%' }} />
              <SecondaryButton onClick={handleRemoveImage} sx={{ p: 1 }} aria-label="Remove image">
                <X size={20} />
              </SecondaryButton>
            </Box>
          )}
        </Box>
        {imageError && (
          <ErrorContainer severity="error">
            <AlertTitle>Image Upload Error</AlertTitle>
            <Typography>{imageError}</Typography>
            <Tooltip title="Copy error details">
              <IconButton
                onClick={() => navigator.clipboard.writeText(`Image Upload Error: ${imageError}\nTime: ${new Date().toLocaleString()}`)}
                sx={{ mt: 1, color: theme.palette.primary.main }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </ErrorContainer>
        )}
      </Box>
      <ImageEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        imageSrc={editorImageSrc}
        onSave={handleSaveCroppedImage}
      />
      <RemoveDialog open={openRemoveDialog} onClose={() => setOpenRemoveDialog(false)}>
        <DialogTitle>Remove Image</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            Are you sure you want to remove this image?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setOpenRemoveDialog(false)}>Cancel</SecondaryButton>
          <CustomButton onClick={confirmRemoveImage}>Remove</CustomButton>
        </DialogActions>
      </RemoveDialog>
    </>
  );
};

const BotCreativityStep = ({ focusRef }) => {
  const { creativityLevel, setCreativityLevel, creativityError, setCreativityError } = useWizard();

  const handleCreativityChange = (event) => {
    setCreativityLevel(event.target.value);
    setCreativityError('');
  };

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Set Bot Creativity
      </Typography>
      <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
        Choose how creative your bot should be.
      </Typography>
      <RadioGroup value={creativityLevel} onChange={handleCreativityChange} sx={{ gap: 2 }}>
        {creativityOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<CustomRadio />}
            label={option.label}
            sx={{
              bgcolor: 'rgba(59,44,91,0.85)',
              p: 2,
              borderRadius: '8px',
              color: theme.palette.text.primary,
              border: `2px solid ${creativityLevel === option.value ? theme.palette.secondary.main : 'transparent'}`,
              '&:hover': { bgcolor: `${theme.palette.text.secondary}20` },
            }}
            aria-label={`Select ${option.label} creativity level`}
          />
        ))}
      </RadioGroup>
      <Typography variant="body2" sx={{ mt: 3, color: '#9ca3af', fontStyle: 'italic' }}>
        Upgrade to unlock more creativity options and specify industry knowledge for enhanced performance!
      </Typography>
      {creativityError && (
        <ErrorContainer severity="error">
          <AlertTitle>Creativity Selection Error</AlertTitle>
          <Typography>{creativityError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Creativity Selection Error: ${creativityError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      )}
    </Box>
  );
};

const DocumentUpload = ({ setDisableBack, focusRef }) => {
  const { document: uploadedFile, setDocument: setUploadedFile, setDocumentError, documentError, setExtractedText, sessionId, setFilePath } = useWizard();
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [extractedMessage, setExtractedMessage] = useState('');
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const extractTextUrl = import.meta.env.VITE_EXTRACT_TEXT_URL;
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!extractTextUrl) {
      setDocumentError('Configuration error: Text extraction service unavailable');
    }
  }, [extractTextUrl, setDocumentError]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setDocumentError('Please select a document');
      return;
    }

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const maxSizeMB = 5;

    if (!allowedTypes.includes(file.type)) {
      setDocumentError('Invalid file type. Use PDF, Word, or Text (.txt).');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setDocumentError(`File size exceeds ${maxSizeMB}MB limit`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (!sessionId) {
      setDocumentError('Critical error: Session unavailable');
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setDocumentError('');
    setExtractedMessage('');
    setFilePath(null);
    setDisableBack(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);

    try {
      const response = await fetch(extractTextUrl, {
        method: 'POST',
        body: formData,
        signal: AbortSignal.timeout(30000),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Upload failed with status ${response.status}`);
      }

      setExtractedText(result.text || '');
      setUploadedFile(file);
      setFilePath(result.filePath || null);
      setExtractedMessage('Document processed successfully!');
      setIsProcessed(true);
    } catch (error) {
      setDocumentError(`Failed to upload document: ${error.message}`);
      setUploadedFile(null);
      setFilePath(null);
    } finally {
      setIsUploading(false);
      setDisableBack(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setOpenRemoveDialog(true);
  };

  const confirmRemoveFile = () => {
    setUploadedFile(null);
    setDocumentError('');
    setExtractedText('');
    setExtractedMessage('');
    setFilePath(null);
    setIsProcessed(false);
    setDisableBack(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setOpenRemoveDialog(false);
  };

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Upload Business Content
      </Typography>
      <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
        Upload a PDF, Word, or Text document (max 5MB).
      </Typography>
      <Box sx={{ width: '100%', bgcolor: 'rgba(59,44,91,0.85)', p: 4, borderRadius: '12px', border: `2px dashed ${theme.palette.text.secondary}`, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <Upload sx={{ color: theme.palette.secondary.main }} size={24} />
          <Typography sx={{ color: theme.palette.text.primary }}>
            {uploadedFile ? uploadedFile.name : 'Browse or Drag Document'}
          </Typography>
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          hidden
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          disabled={isProcessed || isUploading}
          aria-label="Upload document"
        />
        <CustomButton startIcon={<Upload size={20} sx={{ color: '#fff' }} />} onClick={() => fileInputRef.current.click()} disabled={isUploading}>
          Upload Document
        </CustomButton>
        {uploadedFile && !isUploading && (
          <Box sx={{ mt: 2, bgcolor: `${theme.palette.text.secondary}20`, p: 2, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: theme.palette.text.primary, maxWidth: '70%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {uploadedFile.name}
            </Typography>
            <SecondaryButton onClick={handleRemoveFile} sx={{ p: 1 }} disabled={isUploading} aria-label="Remove document">
              <X size={20} />
            </SecondaryButton>
          </Box>
        )}
      </Box>
      {documentError && (
        <ErrorContainer severity="error">
          <AlertTitle>Document Upload Error</AlertTitle>
          <Typography>{documentError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Document Upload Error: ${documentError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      )}
      {isUploading && <BookLoader />}
      {extractedMessage && !documentError && (
        <Typography sx={{ color: theme.palette.success.main, textShadow: '0 0 4px rgba(0,0,0,0.3)' }}>{extractedMessage}</Typography>
      )}
      <RemoveDialog open={openRemoveDialog} onClose={() => setOpenRemoveDialog(false)}>
        <DialogTitle>Remove Document</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            Are you sure you want to remove this document?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setOpenRemoveDialog(false)}>Cancel</SecondaryButton>
          <CustomButton onClick={confirmRemoveFile}>Remove</CustomButton>
        </DialogActions>
      </RemoveDialog>
    </Box>
  );
};

const CustomWelcomeMessage = ({ focusRef }) => {
  const { welcomeMessage, setWelcomeMessage, setWelcomeMessageError, welcomeMessageError } = useWizard();
  const [localError, setLocalError] = useState('');
  const [isEdited, setIsEdited] = useState(false);

  const handleWelcomeMessageChange = (event) => {
    const value = event.target.value;
    setWelcomeMessage(value);
    setIsEdited(!!value);
    setWelcomeMessageError('');
    if (!value) {
      setLocalError('Please enter a welcome message');
    } else if (value.length > 200) {
      setLocalError('Message cannot exceed 200 characters');
    } else {
      setLocalError('');
    }
  };

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center', position: 'relative' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Craft a Welcome Message
      </Typography>
      <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
        How should your bot greet users?
      </Typography>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <CustomTextField
          label="Welcome Message"
          value={welcomeMessage}
          onChange={handleWelcomeMessageChange}
          variant="outlined"
          placeholder="E.g., Hello! I'm here to assist you."
          multiline
          rows={4}
          error={!!welcomeMessageError || !!localError}
          helperText={welcomeMessageError || localError}
          inputProps={{ maxLength: 200 }}
          aria-describedby="welcome-message-error"
          autoFocus
        />
        {welcomeMessage && !welcomeMessageError && !localError && (
          <CheckCircle
            sx={{
              position: 'absolute',
              bottom: '-30px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: isEdited ? theme.palette.success.main : theme.palette.text.primary,
              transition: 'color 0.3s ease',
            }}
          />
        )}
      </Box>
      {(welcomeMessageError || localError) && (
        <ErrorContainer severity="error">
          <AlertTitle>Welcome Message Error</AlertTitle>
          <Typography>{welcomeMessageError || localError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Welcome Message Error: ${welcomeMessageError || localError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      )}
    </Box>
  );
};

const ChatbotToneConfiguration = ({ focusRef }) => {
  const { chatbotTone, setChatbotTone, setChatbotToneError, chatbotToneError } = useWizard();

  const handleChatbotToneChange = (event) => {
    setChatbotTone(event.target.value);
    setChatbotToneError('');
  };

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Set Your Bot’s Tone
      </Typography>
      <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
        Choose a tone for your bot’s personality.
      </Typography>
      <RadioGroup value={chatbotTone} onChange={handleChatbotToneChange} sx={{ gap: 2 }}>
        {toneOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<CustomRadio />}
            label={option.label}
            sx={{
              bgcolor: 'rgba(59,44,91,0.85)',
              p: 2,
              borderRadius: '8px',
              color: theme.palette.text.primary,
              border: `2px solid ${chatbotTone === option.value ? theme.palette.secondary.main : 'transparent'}`,
              '&:hover': { bgcolor: `${theme.palette.text.secondary}20` },
            }}
            aria-label={`Select ${option.label} tone`}
          />
        ))}
      </RadioGroup>
      {chatbotToneError && (
        <ErrorContainer severity="error">
          <AlertTitle>Tone Selection Error</AlertTitle>
          <Typography>{chatbotToneError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Tone Selection Error: ${chatbotToneError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      )}
    </Box>
  );
};

const FinishScreen = ({ focusRef }) => {
  const { botType, document: uploadedFile, welcomeMessage, chatbotTone, botName, imagePreview, creativityLevel, submitError, savedBotId, botCount } = useWizard();
  const [showConfetti, setShowConfetti] = useState(false);
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    if (!submitError) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [submitError]);

  const handleViewBot = () => {
    const destination = botCount > 1 ? '/dashboard/bots' : '/dashboard/bots';
    navigate(destination, { state: { activeDocId: savedBotId, isNewConfig: true }, replace: true });
  };

  return (
    <Box ref={focusRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
      {showConfetti && (
        <Confetti
          width={dimensions.width}
          height={dimensions.height}
          recycle={false}
          numberOfPieces={200}
          colors={['#3b82f6', '#ec4899', '#a855f7']}
        />
      )}
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        {submitError ? 'Setup Failed' : 'Your Bot Is Ready!'}
      </Typography>
      {submitError ? (
        <ErrorContainer severity="error">
          <AlertTitle>Setup Error</AlertTitle>
          <Typography>{submitError}</Typography>
          <Tooltip title="Copy error details">
            <IconButton
              onClick={() => navigator.clipboard.writeText(`Setup Error: ${submitError}\nTime: ${new Date().toLocaleString()}`)}
              sx={{ mt: 1, color: theme.palette.primary.main }}
            >
              <Copy size={20} />
            </IconButton>
          </Tooltip>
        </ErrorContainer>
      ) : (
        <>
          <Typography sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
            Here’s a summary of your bot’s configuration:
          </Typography>
          <Box
            sx={{
              width: '100%',
              bgcolor: 'rgba(59,44,91,0.85)',
              backdropFilter: 'blur(12px)',
              p: 3,
              borderRadius: '12px',
              border: `2px solid ${theme.palette.text.secondary}`,
              color: theme.palette.text.primary,
            }}
            role="region"
            aria-label="Bot configuration summary"
          >
            <Typography><strong>Bot Type:</strong> {botType || 'Text'}</Typography>
            <Typography><strong>Bot Name:</strong> {botName || 'VuxoraAI'}</Typography>
            {uploadedFile && <Typography><strong>Document:</strong> {uploadedFile.name}</Typography>}
            {welcomeMessage && <Typography><strong>Welcome Message:</strong> {welcomeMessage}</Typography>}
            {chatbotTone && <Typography><strong>Tone:</strong> {toneOptions.find((o) => o.value === chatbotTone)?.label}</Typography>}
            {imagePreview && (
              <Typography>
                <strong>Image:</strong>
                <Avatar src={imagePreview} sx={{ width: 32, height: 32, display: 'inline-block', ml: 1, verticalAlign: 'middle', borderRadius: '50%' }} />
              </Typography>
            )}
            {creativityLevel && (
              <Typography>
                <strong>Creativity:</strong> {creativityOptions.find((o) => o.value === creativityLevel)?.label}
              </Typography>
            )}
          </Box>
          <CustomButton onClick={handleViewBot} aria-label="View your bot">
            View Bot
          </CustomButton>
        </>
      )}
    </Box>
  );
};

// Main Wizard component
const Wizard = () => {
  const {
    sessionId, setSessionId, activeStep, setActiveStep,
    botType, setBotType, botTypeError, setBotTypeError,
    document: uploadedFile, filePath, setFilePath, setDocument: setUploadedFile,
    documentError, setDocumentError, setExtractedText,
    welcomeMessage, setWelcomeMessage, welcomeMessageError, setWelcomeMessageError,
    chatbotTone, setChatbotTone, chatbotToneError, setChatbotToneError,
    botName, setBotName, botNameError, setBotNameError,
    imageFile, setImageFile, imagePreview, setImagePreview, imageError, setImageError,
    creativityLevel, setCreativityLevel, creativityError, setCreativityError,
    submitError, setSubmitError, resetWizardContext,
    savedBotId, setSavedBotId, botCount, setBotCount,
  } = useWizard();

  const { auth, user, loadingAuth } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const [disableBack, setDisableBack] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalData, setLimitModalData] = useState({ maxBots: 0 });
  const [initialError, setInitialError] = useState(null);
  const [wizardStatus, setWizardStatus] = useState('initializing');
  const [toastError, setToastError] = useState('');
  const [showBotSelection, setShowBotSelection] = useState(true);
  const focusRef = useRef(null);

  const saveConfigUrl = import.meta.env.VITE_SAVE_CONFIG_URL;
  const imageUploadUrl = import.meta.env.VITE_IMAGE_UPLOAD_URL;
  const planStatusUrl = import.meta.env.VITE_GET_USER_PLAN_STATUS_URL;

  const getHeaders = useCallback(async (contentType = 'application/json') => {
    try {
      const headers = { 'Content-Type': contentType };
      if (auth?.currentUser) {
        const idToken = await auth.currentUser.getIdToken(true);
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      return headers;
    } catch (error) {
      throw new Error(`Authentication error: ${error.message}`);
    }
  }, [auth]);

  const uploadImageToServer = useCallback(async (imageFile, sessionId) => {
    if (!imageFile) {
      setImageError('No image file provided');
      return null;
    }
    if (!imageUploadUrl) {
      setImageError('Image upload service not configured');
      return null;
    }

    const formData = new FormData();
    formData.append('image', imageFile, `avatar-${sessionId || Date.now()}.png`);
    formData.append('sessionId', sessionId);

    try {
      const headers = await getHeaders();
      delete headers['Content-Type'];
      if (!auth?.currentUser) delete headers['Authorization'];
      const response = await fetch(imageUploadUrl, {
        method: 'POST',
        headers,
        body: formData,
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        const imgErrorResult = await response.json().catch(() => ({ error: `Image upload failed (${response.status})` }));
        throw new Error(imgErrorResult.error || `Image upload failed (${response.status})`);
      }
      const result = await response.json();
      return result.tempImagePath || null;
    } catch (error) {
      setImageError(`Image upload failed: ${error.message}`);
      return null;
    }
  }, [imageUploadUrl, getHeaders, setImageError, auth]);

  const finalizeBotConfiguration = useCallback(async (configData) => {
    setIsSubmitting(true);
    setSubmitError('');

    const {
      sessionId: finalizeSessionId,
      botType: finalizeBotType,
      botName: finalizeBotName,
      welcomeMessage: finalizeWelcomeMessage,
      chatbotTone: finalizeChatbotTone,
      documentName: finalizeDocumentName,
      documentPath: finalizeDocumentPath,
      creativityLevel: finalizeCreativityLevel,
      imageFile: finalizeImageFile,
    } = configData;

    if (!saveConfigUrl) {
      setSubmitError('Configuration error: Save service unavailable');
      setIsSubmitting(false);
      return;
    }

    if (!finalizeSessionId) {
      setSubmitError('Invalid session ID');
      setIsSubmitting(false);
      return;
    }

    let tempImagePath = null;
    if (finalizeImageFile) {
      try {
        tempImagePath = await uploadImageToServer(finalizeImageFile, finalizeSessionId);
      } catch (error) {
        setSubmitError(`Image upload failed: ${error.message}`);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const configurationPayload = {
        sessionId: finalizeSessionId,
        botType: finalizeBotType || 'text',
        botName: finalizeBotName || 'VuxoraAI',
        welcomeMessage: finalizeWelcomeMessage,
        chatbotTone: finalizeChatbotTone || 'friendly',
        documentName: finalizeDocumentName,
        documentPath: finalizeDocumentPath,
        creativityLevel: finalizeCreativityLevel || 'balanced',
        tempImagePath,
      };

      const configHeaders = await getHeaders();
      const response = await fetch(saveConfigUrl, {
        method: 'POST',
        headers: configHeaders,
        body: JSON.stringify(configurationPayload),
        signal: AbortSignal.timeout(15000),
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({ error: `Save failed (${response.status})`, code: 'UNKNOWN_ERROR' }));
        if (response.status === 409) {
          setSubmitError('Session expired. Please start over.');
          navigate('/wizard', { replace: true });
          return;
        }
        if (errorResult.code === 'BOT_LIMIT_REACHED' || response.status === 403 || response.status === 402) {
          setLimitModalData({ maxBots: errorResult.maxBots || 'your plan\'s' });
          setShowLimitModal(true);
          return;
        }
        throw new Error(errorResult.error || `Save failed (${response.status})`);
      }

      const resultData = await response.json();
      const newBotId = resultData?.botId;
      const newBotCount = resultData?.botCount || 1;

      if (!newBotId) {
        throw new Error('Bot saved, but no Bot ID returned');
      }

      if (typeof setSavedBotId !== 'function') {
        throw new Error('setSavedBotId is not a function');
      }

      setSavedBotId(newBotId);
      setBotCount(newBotCount);
      sessionStorage.setItem('activeDocId', newBotId);
      sessionStorage.removeItem('sessionId');
      resetWizardContext();
      setActiveStep(6);
    } catch (error) {
      setSubmitError(`Failed to save bot: ${error.message}`);
      console.error('Finalize Bot Error:', error);
      setActiveStep(6);
    } finally {
      setIsSubmitting(false);
    }
  }, [auth, saveConfigUrl, navigate, setActiveStep, setSubmitError, getHeaders, resetWizardContext, setSavedBotId, setBotCount, uploadImageToServer]);

  useEffect(() => {
    if (loadingAuth) {
      setWizardStatus('initializing');
      return;
    }

    let currentSessionId = sessionStorage.getItem('sessionId');
    if (!currentSessionId || currentSessionId === 'undefined') {
      currentSessionId = uuidv4();
      sessionStorage.setItem('sessionId', currentSessionId);
    }
    setSessionId(currentSessionId);

    const checkPlanAndProceed = async (currentUser) => {
      if (!currentUser) {
        setWizardStatus('ready');
        return;
      }

      setWizardStatus('checking_plan');
      setInitialError(null);

      if (!planStatusUrl) {
        setInitialError('Configuration error: Plan status service unavailable');
        setWizardStatus('error');
        return;
      }

      try {
        const headers = await getHeaders();
        const response = await fetch(planStatusUrl, {
          headers,
          signal: AbortSignal.timeout(10000),
        });
        if (!response.ok) {
          throw new Error(`Plan check failed with status ${response.status}`);
        }
        const planData = await response.json();

        if (planData.success && planData.userPlan?.limits) {
          const { currentBots, maxBots } = planData.userPlan.limits;
          if (currentBots >= maxBots) {
            setLimitModalData({ maxBots });
            setWizardStatus('limit_reached');
            setShowLimitModal(true);
          } else {
            setWizardStatus('ready');
          }
        } else {
          throw new Error('Invalid plan data');
        }
      } catch (error) {
        setInitialError(`Failed to verify plan: ${error.message}`);
        setWizardStatus('error');
      }
    };

    checkPlanAndProceed(user);
  }, [loadingAuth, user, navigate, setSessionId, planStatusUrl, getHeaders]);

  useEffect(() => {
    if (!saveConfigUrl) {
      setInitialError('Configuration error: Save configuration URL not set');
      console.error('VITE_SAVE_CONFIG_URL not set');
    }
    if (!imageUploadUrl) {
      setInitialError('Configuration error: Image upload URL not set');
      console.error('VITE_IMAGE_UPLOAD_URL not set');
    }
    if (!planStatusUrl) {
      setInitialError('Configuration error: Plan status URL not set');
      console.error('VITE_GET_USER_PLAN_STATUS_URL not set');
    }
  }, [saveConfigUrl, imageUploadUrl, planStatusUrl, setInitialError]);

  useEffect(() => {
    const { state } = location;
    if (state?.postLoginAction === 'finalizeBot' && state?.wizardData) {
      navigate(location.pathname, { replace: true, state: {} });
      finalizeBotConfiguration(state.wizardData);
    }
  }, [location.state, navigate, finalizeBotConfiguration]);

  useEffect(() => {
    if (submitError || initialError) {
      setToastError(submitError || initialError);
      const timer = setTimeout(() => setToastError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [submitError, initialError]);

  useEffect(() => {
    if (focusRef.current) {
      const firstFocusable = focusRef.current.querySelector('input, button, [tabindex="0"]');
      if (firstFocusable) firstFocusable.focus();
    }
  }, [activeStep, showBotSelection]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  const renderLimitModal = () => (
    <Dialog
      open={showLimitModal}
      onClose={() => setShowLimitModal(false)}
      aria-labelledby="limit-dialog-title"
      PaperProps={{ sx: { bgcolor: 'rgba(59,44,91,0.85)', backdropFilter: 'blur(12px)', borderRadius: '16px', border: `2px solid ${theme.palette.text.secondary}` } }}
    >
      <DialogTitle id="limit-dialog-title" sx={{ color: theme.palette.text.primary, textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
        Bot Limit Reached
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: theme.palette.text.primary, opacity: 0.8 }}>
          You’ve reached the limit of {limitModalData.maxBots} bots. Upgrade your plan or manage existing bots.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ pb: 3, justifyContent: 'center', gap: 2 }}>
        <SecondaryButton onClick={() => { setShowLimitModal(false); navigate('/dashboard/bots'); }}>
          Manage Bots
        </SecondaryButton>
        <CustomButton onClick={() => { setShowLimitModal(false); navigate('/plans'); }}>
          Upgrade Plan
        </CustomButton>
      </DialogActions>
    </Dialog>
  );

  const handleBotTypeSelect = (type) => {
    setBotType(type);
    if (type === 'phone') {
      navigate('/plans');
    } else {
      setShowBotSelection(false);
    }
  };

  const handleNext = debounce(async () => {
    setSubmitError('');
    let isValid = true;

    if (activeStep === 0) {
      if (!uploadedFile) {
        setDocumentError('Please upload a document to continue');
        isValid = false;
      }
    } else if (activeStep === 1) {
      if (!welcomeMessage) {
        setWelcomeMessageError('Please enter a welcome message');
        isValid = false;
      } else if (welcomeMessage.length > 200) {
        setWelcomeMessageError('Message cannot exceed 200 characters');
        isValid = false;
      }
    } else if (activeStep === 2) {
      if (!chatbotTone) {
        setChatbotToneError('Please select a tone');
        isValid = false;
      }
    } else if (activeStep === 3) {
      if (!botName) {
        setBotNameError('Please enter a bot name');
        isValid = false;
      } else if (botName.length > 50) {
        setBotNameError('Bot name cannot exceed 50 characters');
        isValid = false;
      } else if (!/^[a-zA-Z0-9\s-_]+$/.test(botName)) {
        setBotNameError('Use only letters, numbers, spaces, hyphens, or underscores');
        isValid = false;
      }
    } else if (activeStep === 5) {
      if (!creativityLevel) {
        setCreativityError('Please select a creativity level');
        isValid = false;
      } else {
        setIsSubmitting(true);
        let tempImagePath = null;
        try {
          if (imageFile) {
            if (!imageUploadUrl) {
              throw new Error('Image upload service unavailable');
            }
            tempImagePath = await uploadImageToServer(imageFile, sessionId);
          }

          if (auth?.currentUser) {
            await finalizeBotConfiguration({
              sessionId,
              botType,
              botName,
              welcomeMessage,
              chatbotTone,
              documentName: uploadedFile?.name,
              documentPath: filePath,
              creativityLevel,
              imageFile,
            });
          } else {
            const wizardData = {
              sessionId,
              botType,
              botName,
              welcomeMessage,
              chatbotTone,
              documentName: uploadedFile?.name,
              documentPath: filePath,
              creativityLevel,
              imageFile,
            };
            navigate('/login', {
              replace: true,
              state: { postLoginAction: 'finalizeBot', wizardData },
            });
          }
        } catch (error) {
          setSubmitError(`Setup failed: ${error.message}`);
          console.error('Setup Error:', error);
          setActiveStep(6);
        } finally {
          setIsSubmitting(false);
        }
        return;
      }
    }

    if (isValid) {
      setActiveStep((prev) => prev + 1);
    }
  }, 300);

  const handleBack = () => {
    if (activeStep > 0 && !disableBack) {
      if (activeStep === 1) setDocumentError('');
      if (activeStep === 2) setWelcomeMessageError('');
      if (activeStep === 3) setChatbotToneError('');
      if (activeStep === 4) setBotNameError('');
      if (activeStep === 5) setImageError('');
      if (activeStep === 6) setCreativityError('');
      if (activeStep === 7) setSubmitError('');
      setActiveStep((prev) => prev - 1);
    }
  };

  const renderWizardContent = () => {
    switch (wizardStatus) {
      case 'initializing':
      case 'checking_plan':
        return <BookLoader />;
      case 'error':
        return (
          <ErrorContainer severity="error">
            <AlertTitle>Initialization Error</AlertTitle>
            <Typography>{initialError || 'An error occurred during setup'}</Typography>
            <Tooltip title="Copy error details">
              <IconButton
                onClick={() => navigator.clipboard.writeText(`Initialization Error: ${initialError}\nTime: ${new Date().toLocaleString()}`)}
                sx={{ mt: 1, color: theme.palette.primary.main }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </ErrorContainer>
        );
      case 'limit_reached':
        return (
          <ErrorContainer severity="error">
            <AlertTitle>Bot Limit Reached</AlertTitle>
            <Typography>Please check the bot limit information.</Typography>
            <Tooltip title="Copy error details">
              <IconButton
                onClick={() => navigator.clipboard.writeText(`Bot Limit Reached: Limit of ${limitModalData.maxBots} bots\nTime: ${new Date().toLocaleString()}`)}
                sx={{ mt: 1, color: theme.palette.primary.main }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </ErrorContainer>
        );
      case 'ready':
        if (showBotSelection) {
          return <BotTypeSelectionScreen onSelect={handleBotTypeSelect} focusRef={focusRef} />;
        }
        return (
          <CardContainer>
            <CustomStepper role="navigation" aria-label="Wizard steps">
              {steps.map((step, index) => {
                const isSetup = index === 0;
                const isConfigure = index === 1;
                const isFinish = index === 2;
                const isActive = (isSetup && activeStep === 0) ||
                  (isConfigure && activeStep >= 1 && activeStep <= 5) ||
                  (isFinish && activeStep === 6);
                const isCompleted = (isSetup && activeStep > 0) ||
                  (isConfigure && activeStep > 5) ||
                  (isFinish && activeStep > 6);
                return (
                  <Box
                    key={step.label}
                    className={`step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                    sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
                  >
                    <Box className="step-number">
                      {isCompleted ? <CheckCircle size={16} color="#fff" /> : index + 1}
                    </Box>
                    <Typography>{step.label}</Typography>
                    {index < steps.length - 1 && (
                      <Box className={`progress-line ${isCompleted || isActive ? 'filled' : ''}`} />
                    )}
                  </Box>
                );
              })}
            </CustomStepper>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                style={{ width: '100%', position: 'relative' }}
              >
                {(() => {
                  switch (activeStep) {
                    case 0: return <DocumentUpload setDisableBack={setDisableBack} focusRef={focusRef} />;
                    case 1: return <CustomWelcomeMessage focusRef={focusRef} />;
                    case 2: return <ChatbotToneConfiguration focusRef={focusRef} />;
                    case 3: return <BotNameStep focusRef={focusRef} />;
                    case 4: return <ImageUploadStep focusRef={focusRef} />;
                    case 5: return <BotCreativityStep focusRef={focusRef} />;
                    case 6: return <FinishScreen focusRef={focusRef} />;
                    default:
                      return (
                        <ErrorContainer severity="error">
                          <AlertTitle>Invalid Step</AlertTitle>
                          <Typography>Invalid wizard step. Please restart the process.</Typography>
                          <Tooltip title="Copy error details">
                            <IconButton
                              onClick={() => navigator.clipboard.writeText(`Invalid Step Error: Step ${activeStep} is not recognized\nTime: ${new Date().toLocaleString()}`)}
                              sx={{ mt: 1, color: theme.palette.primary.main }}
                            >
                              <Copy size={20} />
                            </IconButton>
                          </Tooltip>
                        </ErrorContainer>
                      );
                  }
                })()}
              </motion.div>
            </AnimatePresence>
            {activeStep < 6 && (
              <ButtonContainer>
                <SecondaryButton onClick={handleBack} disabled={activeStep === 0 || disableBack || isSubmitting} aria-label="Back to previous step">
                  Back
                </SecondaryButton>
                <CustomButton onClick={handleNext} disabled={isSubmitting} aria-label={activeStep === 5 ? 'Finish setup' : 'Next step'}>
                  {isSubmitting ? 'Processing...' : activeStep === 5 ? 'Finish' : 'Next'}
                </CustomButton>
              </ButtonContainer>
            )}
          </CardContainer>
        );
      default:
        return (
          <ErrorContainer severity="error">
            <AlertTitle>Unknown State</AlertTitle>
            <Typography>Unknown wizard state. Please reload the page.</Typography>
            <Tooltip title="Copy error details">
              <IconButton
                onClick={() => navigator.clipboard.writeText(`Unknown State Error: Wizard status ${wizardStatus}\nTime: ${new Date().toLocaleString()}`)}
                sx={{ mt: 1, color: theme.palette.primary.main }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </ErrorContainer>
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <WizardErrorBoundary>
        <MainContainer sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          <Particles id="wizard-particles" init={particlesInit} options={particlesOptions} style={{ position: 'absolute', top: 0, left: 0, zIndex: -1 }} />
          {renderWizardContent()}
          {renderLimitModal()}
          <AnimatePresence>
            {toastError && (
              <ToastContainer
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.3 }}
                role="alert"
                aria-live="assertive"
              >
                <Typography>{toastError}</Typography>
              </ToastContainer>
            )}
          </AnimatePresence>
        </MainContainer>
      </WizardErrorBoundary>
    </ThemeProvider>
  );
};

export default Wizard;